import React, { useState, useEffect } from 'react';

import { getLogs, clearLogs, downloadLogs, filterLogs } from '../utils/logger';
import { SEO } from '../shared/components/SEO';

const DebugLogs: React.FC = () => {
  const [logs, setLogs] = useState(getLogs());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [packageFilter, setPackageFilter] = useState<string>('');

  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      setLogs(getLogs());
    }, 1000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleClear = () => {
    clearLogs();
    setLogs([]);
  };

  const handleDownload = () => {
    downloadLogs();
  };

  const handleRefresh = () => {
    setLogs(getLogs());
  };

  const filteredLogs = filterLogs({
    level: levelFilter || undefined,
    package: packageFilter || undefined
  });

  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <SEO
        title="Debug Logs — GoGrabJob"
        description="Internal OAuth debug logs for GoGrabJob."
        canonical="https://www.gograbjob.com/debug-logs"
        noIndex
      />
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">OAuth Debug Logs</h1>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 text-white">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="form-checkbox"
              />
              Auto-refresh
            </label>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
            >
              Refresh
            </button>
            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
            >
              Download
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex gap-4 mb-4">
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="px-3 py-2 bg-gray-700 text-white rounded"
            >
              <option value="">All Levels</option>
              <option value="TRACE">TRACE</option>
              <option value="DEBUG">DEBUG</option>
              <option value="INFO">INFO</option>
              <option value="WARN">WARN</option>
              <option value="ERROR">ERROR</option>
              <option value="AUDIT">AUDIT</option>
              <option value="PERF">PERF</option>
            </select>
            <select
              value={packageFilter}
              onChange={(e) => setPackageFilter(e.target.value)}
              className="px-3 py-2 bg-gray-700 text-white rounded"
            >
              <option value="">All Packages</option>
              <option value="OAuth">OAuth</option>
              <option value="Auth">Auth</option>
              <option value="Database">Database</option>
              <option value="API">API</option>
              <option value="UI">UI</option>
            </select>
            <div className="text-sm text-gray-400 flex items-center">
              Total: {logs.length} | Filtered: {filteredLogs.length}
            </div>
          </div>

          {filteredLogs.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No logs match your filters. Try signing in with OAuth.</p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {filteredLogs.map((log, index) => (
                <div
                  key={index}
                  className={`p-3 rounded border-l-4 ${
                    log.level === 'ERROR'
                      ? 'bg-red-900/20 border-red-500'
                      : log.level === 'WARN'
                      ? 'bg-yellow-900/20 border-yellow-500'
                      : log.level === 'AUDIT'
                      ? 'bg-purple-900/20 border-purple-500'
                      : log.level === 'PERF'
                      ? 'bg-blue-900/20 border-blue-500'
                      : 'bg-green-900/20 border-green-500'
                  }`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex gap-2">
                      <span className="font-mono text-xs text-gray-400">{log.timestamp}</span>
                      <span className="text-xs font-semibold text-cyan-400">{log.package}</span>
                      {log.caller && <span className="text-xs text-gray-500">{log.caller}</span>}
                    </div>
                    <span
                      className={`text-xs font-bold uppercase ${
                        log.level === 'ERROR'
                          ? 'text-red-400'
                          : log.level === 'WARN'
                          ? 'text-yellow-400'
                          : log.level === 'AUDIT'
                          ? 'text-purple-400'
                          : log.level === 'PERF'
                          ? 'text-blue-400'
                          : 'text-green-400'
                      }`}
                    >
                      {log.level}
                    </span>
                  </div>
                  <div className="text-white font-medium mb-1">{log.message}</div>
                  {log.data && (
                    <pre className="text-xs text-gray-300 bg-gray-900 p-2 rounded overflow-x-auto">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DebugLogs;

